/**
 * /workspaces - the tenant picker. Two modes:
 *
 *   1. Pending-session mode: no App JWT yet. We got here because /login
 *      or the OAuth callback returned `needsTenantSelection`. The pending
 *      token lives in sessionStorage (tab-scoped; 5-min TTL). Clicking a
 *      tenant calls POST /api/auth/select-tenant, which mints the real
 *      App JWT + refresh cookie.
 *
 *   2. Authed mode: user is signed in and clicked "Switch workspace"
 *      from the profile menu. We already have a valid App JWT; clicking
 *      a tenant calls POST /api/auth/switch-tenant instead.
 *
 * Either way we end up at /dashboard. Also exposes "Create new workspace"
 * to self-serve a fresh tenant on the same account.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Building2, CheckCircle2, Crown, Loader2, Plus, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Page } from '@/components/Page'
import { useToast } from '@/hooks/useToast'
import { useTenant } from '@/hooks/useTenant'
import { tokenManager } from '@/lib/tokenManager'
import { useAuth } from '@/auth/useAuth'
import {
  useMemberships,
  useSelectTenant,
  useSwitchTenant,
  type Membership,
} from '@/auth/useMemberships'
import { authApi } from '@/features/auth/api'
import { CreateWorkspaceModal } from '@/shell/TenantSwitcher'
import { cn } from '@/lib/cn'

const PENDING_SESSION_KEY = 'bk.pending_session'
const PENDING_MEMBERSHIPS_KEY = 'bk.pending_memberships'
const PENDING_EMAIL_KEY = 'bk.pending_email'
const PENDING_NAME_KEY = 'bk.pending_name'

function readPending(): {
  token: string | null
  memberships: Membership[]
  email: string | null
  name: string | null
} {
  try {
    const token = sessionStorage.getItem(PENDING_SESSION_KEY)
    const raw = sessionStorage.getItem(PENDING_MEMBERSHIPS_KEY)
    const memberships = raw ? (JSON.parse(raw) as Membership[]) : []
    return {
      token,
      memberships,
      email: sessionStorage.getItem(PENDING_EMAIL_KEY),
      name: sessionStorage.getItem(PENDING_NAME_KEY),
    }
  } catch {
    return { token: null, memberships: [], email: null, name: null }
  }
}

function clearPending(): void {
  try {
    sessionStorage.removeItem(PENDING_SESSION_KEY)
    sessionStorage.removeItem(PENDING_MEMBERSHIPS_KEY)
    sessionStorage.removeItem(PENDING_EMAIL_KEY)
    sessionStorage.removeItem(PENDING_NAME_KEY)
  } catch {
    /* ignore */
  }
}

/**
 * Two render variants:
 *
 *   - 'standalone' (default) - used by the bare `/workspaces` route
 *     mounted under AuthLayout. Centered card, sign-out / cancel link
 *     at the bottom. This is the post-OAuth and post-multi-tenant-login
 *     surface; the user does NOT have the chrome around them.
 *
 *   - 'embedded' - used by the in-app `/account/workspaces` route
 *     mounted under AppShell. Sits inside the canonical Page chrome
 *     with the sidebar + header still visible. We deliberately suppress
 *     the sign-out link here: the header already exposes Sign Out from
 *     the user menu, and a "Sign out" button hanging in the middle of
 *     a normal in-app page is jarring + double-confirms-bad behaviour.
 */
type Variant = 'standalone' | 'embedded'

interface Props {
  variant?: Variant
}

export function WorkspacePickerPage({ variant = 'standalone' }: Props = {}) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { isAuthenticated } = useAuth()
  // Used by the embedded variant to mark the row the user is already in
  // with a "Current" badge instead of an actionable Switch button. Pure
  // visual hint - the backend rejects switching to the active tenant
  // anyway so this is just preventing a wasted click.
  const activeTenantId = useTenant()

  // Read pending state lazily so a sign-out mid-flow doesn't surprise us.
  const [pending] = useState(() => readPending())
  // Pending mode wins whenever a pending session exists. Reason: the OAuth
  // callback / multi-tenant login path may leave a stale App JWT in
  // localStorage from an earlier session. Treating that as "authed" makes
  // the picker fire useMemberships() with a dead token, get a 401, and
  // bounce to /login - the exact loop reported by users. The pending
  // session is freshly minted (5-min TTL) so trusting it when it exists
  // is correct, and the OAuth handoff also clears the stale localStorage
  // keys defensively.
  const mode: 'pending' | 'authed' = pending.token ? 'pending' : 'authed'
  // Avoid eslint "unused" complaints when the auth context is purely
  // observational here; we used it before.
  void isAuthenticated

  // Authed mode uses live memberships from the API; pending mode uses the
  // snapshot returned by /login (we can't hit /api/auth/memberships without
  // a valid App JWT yet). The `enabled` flag is load-bearing: firing the
  // query in pending mode would 401 and api.ts would redirect to /login.
  const membershipsQ = useMemberships({ enabled: mode === 'authed' })
  const memberships: Membership[] =
    mode === 'authed' ? membershipsQ.data ?? [] : pending.memberships

  const selectMut = useSelectTenant()
  const switchMut = useSwitchTenant()
  const [createOpen, setCreateOpen] = useState(false)
  const [submittingTenant, setSubmittingTenant] = useState<string | null>(null)

  // If we arrived in pending mode but the token is actually gone (e.g.
  // expired between tabs), bounce back to login with a gentle message.
  useEffect(() => {
    if (mode === 'pending' && !pending.token) {
      toast({
        title: 'Sign in again',
        description: 'Your selection window expired. Please sign in again.',
      })
      navigate('/login', { replace: true })
    }
  }, [mode, pending.token, navigate, toast])

  // Single-tenant users shouldn't land here via the post-OAuth picker
  // (no choice to make), so the standalone variant bounces them to the
  // dashboard. The embedded variant skips the bounce: the user clicked
  // "Workspaces" from the menu deliberately, and they should at least
  // see their (one) workspace plus the "Create new workspace" button.
  useEffect(() => {
    if (mode !== 'authed') return
    if (variant === 'embedded') return
    if (membershipsQ.isLoading) return
    if (memberships.length <= 1) navigate('/dashboard', { replace: true })
  }, [mode, membershipsQ.isLoading, memberships.length, navigate, variant])

  const displayEmail = useMemo(() => pending.email ?? '', [pending.email])
  const displayName = useMemo(() => pending.name ?? displayEmail, [pending.name, displayEmail])

  const onPick = async (tenantId: string) => {
    setSubmittingTenant(tenantId)
    try {
      if (mode === 'pending') {
        if (!pending.token) throw new Error('Pending session expired.')
        const res = await selectMut.mutateAsync({
          pendingSessionToken: pending.token,
          tenantId,
        })
        tokenManager.set(res.token)
        if (res.userId) localStorage.setItem('userId', String(res.userId))
        if (res.name) localStorage.setItem('userName', res.name)
        if (res.email) localStorage.setItem('userEmail', res.email)
        clearPending()
        navigate('/dashboard', { replace: true })
      } else {
        await switchMut.mutateAsync(tenantId)
        // useSwitchTenant.onSuccess already navigates.
      }
    } catch (err) {
      const e = err as { body?: { error?: string; code?: string }; message?: string }
      const code = e.body?.code
      if (code === 'PENDING_EXPIRED') {
        clearPending()
        toast({
          title: 'Selection window expired',
          description: 'Please sign in again.',
        })
        navigate('/login', { replace: true })
        return
      }
      toast({
        title: 'Could not switch workspace',
        description: e.body?.error || e.message || 'Unexpected error.',
      })
    } finally {
      setSubmittingTenant(null)
    }
  }

  const abandon = async () => {
    if (mode === 'pending') {
      clearPending()
      navigate('/login', { replace: true })
      return
    }
    try { await authApi.logout() } catch { /* non-fatal */ }
    tokenManager.clear()
    navigate('/login', { replace: true })
  }

  // Body is identical for both variants except for the embedded mode
  // dropping the sign-out link (the header user-menu already exposes
  // Sign Out, and a centered sign-out CTA in the middle of an in-app
  // page is jarring).
  const card = (
    <Card>
      <CardContent className="space-y-5 p-8">
        <header className="space-y-1 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Building2 className="size-6" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">Choose a workspace</h1>
          <p className="text-sm text-muted-foreground">
            {displayName
              ? `Hi ${displayName}, select the workspace you want to work in.`
              : 'Select the workspace you want to work in.'}
          </p>
        </header>

        <div className="space-y-2">
          {mode === 'authed' && membershipsQ.isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : memberships.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No active workspaces on this account.
            </p>
          ) : (
            memberships.map((m) => {
              const busy = submittingTenant === m.tenantId
              const anyBusy = !!submittingTenant
              const isCurrent = variant === 'embedded' && activeTenantId === m.tenantId
              return (
                <div
                  key={m.tenantId}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded-md border bg-card px-4 py-3 transition',
                    isCurrent ? 'border-primary/40 bg-primary/5' : 'hover:border-primary/40',
                  )}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium">{m.tenantName}</span>
                      {m.isOwner ? <Crown className="size-3.5 text-amber-500" aria-label="Owner" /> : null}
                      {isCurrent ? (
                        <Badge variant="success" className="shrink-0 gap-1 text-[10px]">
                          <CheckCircle2 className="size-3" aria-hidden /> Current
                        </Badge>
                      ) : null}
                    </div>
                    <div className="block truncate text-xs capitalize text-muted-foreground">
                      {m.roleName.replace(/_/g, ' ')}
                      {m.tenantPlan ? ` · ${m.tenantPlan} plan` : ''}
                    </div>
                  </div>
                  {/* Right-side control: explicit Switch button so the
                      action is discoverable and consistent with how
                      "actionable rows" work elsewhere in the app. The
                      whole row used to be a single click target which
                      hid the call-to-action; now it's a real button. */}
                  {isCurrent ? (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      You&apos;re here
                    </span>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      className="shrink-0 gap-1.5"
                      onClick={() => onPick(m.tenantId)}
                      disabled={anyBusy}
                      aria-label={`Switch to ${m.tenantName}`}
                    >
                      {busy ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <>
                          Switch
                          <ArrowRight className="size-3.5" aria-hidden />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )
            })
          )}
        </div>

        {mode === 'authed' ? (
          <>
            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>
            <Button variant="outline" className="w-full gap-2" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> Create new workspace
            </Button>
          </>
        ) : null}

        {variant === 'standalone' ? (
          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={abandon}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <LogOut className="size-3.5" />
              {mode === 'pending' ? 'Cancel and return to sign in' : 'Sign out'}
            </button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )

  if (variant === 'embedded') {
    // <Page> sits inside AppShell's <main>, which already provides the
    // canonical px/py padding so this surface lines up with Dashboard,
    // Branding, etc. Card stretches across the full content area -
    // matches how the rest of the in-app surfaces (Dashboard, Branding,
    // Profile, Reports) fill the canvas instead of clamping to a
    // narrow column.
    return (
      <Page
        title="Workspaces"
        description="Switch between the workspaces you belong to, or create a new one."
      >
        {card}
        <CreateWorkspaceModal open={createOpen} onClose={() => setCreateOpen(false)} />
      </Page>
    )
  }

  return (
    <div className="mx-auto w-full max-w-xl">
      {card}
      <CreateWorkspaceModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}
